<script setup>
import { ref, computed } from 'vue'
import { Link } from '@inertiajs/vue3'
import { Button } from '@/Components/ui/button'
import { CheckCircle2, ShieldCheck, Award, BookOpen, Plus, Minus, ChevronRight } from 'lucide-vue-next'
import { parents, faq } from '@/routes/index'

/**
 * The parent's objections, answered in one band: what they get, what we can
 * prove, and the four questions they ask before they buy.
 *
 * Every number in the middle card is counted from the database. The concept's
 * "95% of students improve a grade" and "1,000+ students supported" are not
 * here: they are advertising claims in the UK and we cannot substantiate either.
 * "100% DBS checked" stays because it describes the approval process rather than
 * an outcome — a tutor cannot be listed without a check on file.
 */
const props = defineProps({
  figures: {
    type: Object,
    default: () => ({}),
  },
})

const benefits = [
  'Carefully selected, top-achieving tutors',
  'Regular lesson reports and feedback',
  'Track progress and grades over time',
  'Safe, supportive and professional',
  'Cancel or change anytime',
]

const stats = computed(() => [
  {
    icon: ShieldCheck,
    value: '100%',
    label: 'DBS checked before they teach',
  },
  {
    icon: Award,
    value: props.figures?.tutor_count ? String(props.figures.tutor_count) : '—',
    label: 'specialist tutors, each interviewed and grade-verified',
  },
  {
    icon: BookOpen,
    value: props.figures?.subject_count ? String(props.figures.subject_count) : '—',
    label: 'subjects taught, from GCSE to A-Level',
  },
])

/**
 * Four questions, answered about the product as it exists today. Bindle Live is
 * not built yet, so nothing here describes it as though a parent could buy it.
 */
const faqs = [
  {
    q: 'How are tutors selected?',
    a: 'Every tutor is a graduate of, or currently studying at, a top UK university. We verify their degree and their own A-level results, interview them, and check DBS status before they can teach.',
  },
  {
    q: 'How does the subscription work?',
    a: 'You subscribe to a tutor at the weekly price shown on their profile. There is no joining fee and no minimum term — cancel from your account and it stops at the end of the week you have paid for.',
  },
  {
    q: 'Can I change my tutor?',
    a: 'Yes, at any point and with no penalty. Anything already shared with you stays available.',
  },
  {
    q: 'How do lessons happen?',
    a: 'Online, in your browser, at the time you agree with the tutor. Nothing needs installing, and the lesson opens from the schedule in your account.',
  },
]

const open = ref(0)
const toggle = (index) => { open.value = open.value === index ? null : index }
</script>

<template>
  <section id="for-parents" class="scroll-mt-24 pb-14 md:pb-20">
    <div class="container-page grid gap-5 lg:grid-cols-3">
      <!-- For parents -->
      <div class="surface-card p-6">
        <h2 class="text-2xl">Designed for parents</h2>
        <ul class="mt-5 space-y-3">
          <li v-for="benefit in benefits" :key="benefit" class="flex gap-2.5 text-[15px] text-muted">
            <CheckCircle2 class="mt-0.5 size-4.5 shrink-0 text-brand" />
            {{ benefit }}
          </li>
        </ul>
        <Link :href="parents()" class="mt-6 inline-block">
          <Button variant="secondary" size="sm">Explore for parents</Button>
        </Link>
      </div>

      <!-- Proof -->
      <div class="rounded-lg bg-brand p-6 text-brand-contrast">
        <div
          v-for="(stat, index) in stats"
          :key="stat.label"
          :class="['flex items-center gap-4', index > 0 && 'mt-5 border-t border-white/25 pt-5']"
        >
          <component :is="stat.icon" class="size-6.5 shrink-0 opacity-90" />
          <div>
            <strong class="block font-display text-3xl font-bold leading-none">{{ stat.value }}</strong>
            <span class="text-sm opacity-85">{{ stat.label }}</span>
          </div>
        </div>
      </div>

      <!-- FAQ -->
      <div class="surface-card flex flex-col p-6">
        <h2 class="text-2xl">Frequently asked questions</h2>
        <div class="mt-3">
          <div v-for="(item, index) in faqs" :key="item.q" class="border-b border-line last:border-0">
            <button
              type="button"
              class="flex w-full items-center justify-between gap-4 py-3.5 text-left text-[15px] font-semibold"
              :aria-expanded="open === index"
              @click="toggle(index)"
            >
              {{ item.q }}
              <Minus v-if="open === index" class="size-4 shrink-0 text-brand" />
              <Plus v-else class="size-4 shrink-0 text-brand" />
            </button>
            <p v-show="open === index" class="pb-4 text-sm leading-relaxed text-muted">{{ item.a }}</p>
          </div>
        </div>
        <Link :href="faq()" class="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-brand hover:underline">
          View all FAQs <ChevronRight class="size-4" />
        </Link>
      </div>
    </div>
  </section>
</template>
